"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { themes, defaultThemeKey } from "@/config/theme";
import { ensureFontsLoaded } from "@/lib/dynamicFonts";

const ThemeContext = createContext(null);

const STORAGE_KEY = "meowmi-theme";
const MESSAGE_TYPE = "meowmi-theme-override";

export function useTheme() {
  return useContext(ThemeContext);
}

function applyTheme(theme, overrideColors) {
  const root = document.documentElement;
  const { colors, fonts, radius, spacing, effects } = theme;
  const mergedColors = { ...colors, ...(overrideColors || {}) };

  Object.entries(mergedColors).forEach(([key, value]) => {
    root.style.setProperty(`--color-${key}`, value);
  });
  Object.entries(fonts).forEach(([key, value]) => {
    root.style.setProperty(`--font-${key}`, value);
  });
  Object.entries(radius).forEach(([key, value]) => {
    root.style.setProperty(`--radius-${key}`, value);
  });
  Object.entries(spacing).forEach(([key, value]) => {
    root.style.setProperty(`--spacing-${key}`, value);
  });
  root.style.setProperty("--shadow-card", effects.cardShadow);
  root.style.setProperty("--shadow-card-hover", effects.cardHoverShadow);
  root.style.setProperty(
    "--texture-opacity",
    String(effects.bgTexture ? effects.textureOpacity : 0),
  );

  // Lazy-load any Google Fonts this theme needs beyond the default pair
  // (Zen Maru Gothic + Noto Sans TC are already shipped via next/font).
  // See lib/dynamicFonts.js for dedup logic.
  if (Array.isArray(theme.fontUrls) && theme.fontUrls.length > 0) {
    ensureFontsLoaded(theme.fontUrls);
  }
}

function readPreviewKey() {
  if (typeof window === "undefined") return null;
  try {
    const v = new URLSearchParams(window.location.search).get("preview");
    return v && themes[v] ? v : null;
  } catch {
    return null;
  }
}

// SSR and first client render MUST agree on state to avoid hydration mismatch
// (which can silently skip useEffect on the mismatched subtree). So we start
// with lockedThemeKey or defaultThemeKey on both sides, then a client-only
// useEffect upgrades to preview/localStorage after mount.
export default function ThemeProvider({
  children,
  lockedThemeKey = null,
  overrides = null,
}) {
  const baseKey =
    lockedThemeKey && themes[lockedThemeKey] ? lockedThemeKey : defaultThemeKey;
  const [themeKey, setThemeKey] = useState(baseKey);
  const [previewMode, setPreviewMode] = useState(false);
  // draftOverride: live colors received via postMessage from admin editor (iframe preview)
  const [draftOverride, setDraftOverride] = useState(null);
  const currentKeyRef = useRef(baseKey);

   
  useEffect(() => {
    const previewKey = readPreviewKey();
    const effectiveOverrides =
      overrides && typeof overrides === "object" ? overrides : {};
    const pickOverride = (key) => {
      const o = effectiveOverrides[key];
      return o && typeof o === "object" ? o : null;
    };

    if (previewKey) {
      setPreviewMode(true);
      setThemeKey(previewKey);
      currentKeyRef.current = previewKey;
      applyTheme(themes[previewKey], pickOverride(previewKey));
      return;
    }
    if (lockedThemeKey && themes[lockedThemeKey]) {
      setThemeKey(lockedThemeKey);
      currentKeyRef.current = lockedThemeKey;
      applyTheme(themes[lockedThemeKey], pickOverride(lockedThemeKey));
      return;
    }
    const saved =
      typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
    const next = saved && themes[saved] ? saved : baseKey;
    setThemeKey(next);
    currentKeyRef.current = next;
    applyTheme(themes[next], pickOverride(next));
  }, [lockedThemeKey, baseKey, overrides]);

  // Listen to postMessage from the admin editor iframe parent for live colour preview.
  // Message shape: { type: 'meowmi-theme-override', themeKey, colors }
  useEffect(() => {
    function onMessage(event) {
      if (typeof window === "undefined") return;
      if (event.origin !== window.location.origin) return;
      const data = event.data;
      if (!data || data.type !== MESSAGE_TYPE) return;

      const key =
        typeof data.themeKey === "string" && themes[data.themeKey]
          ? data.themeKey
          : null;
      const colors =
        data.colors && typeof data.colors === "object" ? data.colors : null;

      if (key && key !== currentKeyRef.current) {
        setThemeKey(key);
        currentKeyRef.current = key;
        applyTheme(themes[key], colors || {});
        setDraftOverride(colors || {});
        return;
      }

      // Same theme → just re-apply overrides (live colour update while dragging picker)
      setDraftOverride(colors || {});
      applyTheme(themes[currentKeyRef.current], colors || {});
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const switchTheme = useCallback(
    (key) => {
      if (!themes[key]) return;
      if (lockedThemeKey) return;
      if (previewMode) return;
      const effectiveOverrides =
        overrides && typeof overrides === "object" ? overrides : {};
      const ov = effectiveOverrides[key];
      setThemeKey(key);
      currentKeyRef.current = key;
      localStorage.setItem(STORAGE_KEY, key);
      applyTheme(themes[key], ov && typeof ov === "object" ? ov : null);
    },
    [lockedThemeKey, previewMode, overrides],
  );

  return (
    <ThemeContext.Provider
      value={{
        themeKey,
        currentTheme: themes[themeKey],
        switchTheme,
        themes,
        locked: !!lockedThemeKey,
        previewMode,
        draftOverride,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}
